import React from "react";
import { motion } from "framer-motion";
import Card from "../components/Card";
import { Link } from "react-router-dom";
import Button from "../components/Button";
import SEO from "../components/SEO";
import StructuredData, {
  createBreadcrumbSchema,
} from "../components/StructuredData";
import { seoData } from "../utils/seo";

// Viewport settings for better animation triggering
const viewportSettings = {
  once: true,
  amount: 0.1,
  margin: "0px 0px -50px 0px",
};

const Security = () => {
  const securityFeatures = [
    {
      title: "Secure and Reliable Infrastructure",
      description:
        "Estabild uses Amazon Web Services (AWS), exclusively, for the hosting of staging and production environments. AWS data centers are secured with state of the art security mechanisms and SOC 1, SOC 2, and SOC 3 certifications. Databases are backed up in AWS and all the backups are encrypted for additional security.",
      link: {
        text: "Learn more about AWS security",
        url: "https://aws.amazon.com/security/",
      },
    },
    {
      title: "Authentication and User Identity Management",
      description:
        "Estabild partners up with a third-party service Okta, one of the leading cloud identity and access management solutions providers. All the user data are protected with identity management expertise and state of the art security features. We ensure that only the right people can access the information stored in Estabild.",
      link: {
        text: "Learn more about Okta security",
        url: "https://www.okta.com/security/",
      },
    },
    {
      title: "Data Security",
      description:
        "Platform enforces secure password policy to enhance the security of the authentication of the users. Password and sensitive data are stored encrypted using the industry best practices and cryptographic frameworks.",
      link: null,
    },
    {
      title: "Encrypted Data Communication",
      description:
        "All the communication between you and the platform is encrypted and uses strong encryption protocols. This protects the data in transit secure and communication private. The connection to this site is encrypted and authenticated using TLS 1.2, ECDHE_RSA with P-256, and AES 128 GCM.",
      link: null,
    },
  ];

  const securitySeo = seoData.security;
  const structuredData = [
    createBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Security", url: securitySeo.url },
    ]),
  ];

  return (
    <div className="min-h-screen pt-24">
      <SEO
        title={securitySeo.title}
        description={securitySeo.description}
        keywords={securitySeo.keywords}
        url={securitySeo.url}
      />
      <StructuredData data={structuredData} />
      {/* Hero Section */}
      <section className="pt-20 pb-12">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto text-center"
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Security at Estabild
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-4xl mx-auto">
              At Estabild we hold ourselves to industry-leading privacy and
              security standards and take the responsibility of keeping your
              data secure and private seriously.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Security Features Grid */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {securityFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={viewportSettings}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full p-20 hover:shadow-lg transition-shadow duration-300">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-center mb-6">
                    {feature.description}
                  </p>
                  {feature.link && (
                    <div className="text-center">
                      <a
                        href={feature.link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 font-semibold hover:text-primary-700 transition-colors inline-flex items-center gap-2"
                      >
                        {feature.link.text}
                        <span>→</span>
                      </a>
                    </div>
                  )}
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 sm:py-24 lg:py-28 bg-primary-600 text-white">
        <div className="container-custom text-center px-4">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-8 sm:mb-10">
            Ready to streamline your project reporting?
          </h2>
          <Link to="/contact">
            <Button
              variant="secondary"
              className="text-lg sm:text-xl px-8 sm:px-10"
            >
              Get in Touch
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Security;
